import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { auth, authApi } from '@/services/api';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (auth.isAuthenticated()) {
    const role = auth.getRole();
    const staffRoles = ['super_admin', 'admin', 'manager', 'agent'];
    if (staffRoles.includes(role || '')) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password.trim()) {
      setError('Email and password are required');
      return;
    }

    setLoading(true);
    try {
      const res = await authApi.login(email.trim(), password);
      if (res.data) {
        const data = res.data as { token: string; tenant_id: number; user: { role: string } };

        const staffRoles = ['super_admin', 'admin', 'manager', 'agent'];
        if (!staffRoles.includes(data.user.role)) {
          setError('This portal is for staff only. Please use the members login.');
          setLoading(false);
          return;
        }

        auth.setToken(data.token);
        auth.setTenantId(data.tenant_id);
        auth.setRole(data.user.role);
        auth.setUserId((data.user as { id: number }).id);
        window.dispatchEvent(new Event('storage'));
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Login failed. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden bg-background-100">
      {/* Subtle warm ambient glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[350px] rounded-full bg-primary-300/20 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-accent-300/20 blur-3xl pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        {/* Brand */}
        <div className="text-center mb-8">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20minimalist%20geometric%20logo%20mark%20for%20premium%20real%20estate%20brand%2C%20rich%20mustard%20gold%20and%20dark%20charcoal%20tones%2C%20clean%20elegant%20modern%20corporate%20design%2C%20gold%20and%20ivory%20palette%2C%20professional%20premium%20luxury%20identity%20mark%2C%20pure%20white%20background%2C%20highly%20detailed%20vector%20style%20flat%20icon&width=200&height=200&seq=brand-mark-admin-gold-01&orientation=squarish&nocache=true"
            alt="CashMyProperty"
            className="w-12 h-12 rounded-xl object-cover mx-auto mb-3"
          />
          <div className="flex items-center justify-center">
            <span className="font-heading text-foreground-950 font-semibold text-xl tracking-tight">CashMyProperty</span>
            <span className="font-heading font-semibold text-xl tracking-tight text-primary-600">.ai</span>
          </div>
          <p className="text-sm text-foreground-500 mt-1">Admin &amp; Staff Portal</p>
        </div>

        {/* Form card */}
        <div className="bg-background-50 border border-background-200 rounded-2xl p-8 space-y-6 shadow-sm">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-1">Sign In to Admin</h2>
            <p className="text-sm text-foreground-500">Authorized personnel only. All activity is logged.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-start gap-3 p-3 rounded-xl border bg-rose-500/10 border-rose-500/25">
                <i className="ri-error-warning-line mt-0.5 flex-shrink-0 text-rose-600" />
                <p className="text-sm text-rose-600">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider mb-2 text-foreground-500">
                Email Address
              </label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@cashmyproperty.ai"
                  autoComplete="email"
                  className="w-full pl-10 pr-4 py-3 rounded-xl text-sm bg-background-100 border border-background-300 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-colors"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-semibold uppercase tracking-wider mb-2 text-foreground-500">
                Password
              </label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  className="w-full pl-10 pr-11 py-3 rounded-xl text-sm bg-background-100 border border-background-300 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 cursor-pointer text-foreground-400 hover:text-foreground-600 transition-colors"
                  tabIndex={-1}
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-xl font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 bg-primary-500 text-white hover:bg-primary-600"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <i className="ri-shield-check-line" />
                  Sign In to Admin Panel
                </>
              )}
            </button>
          </form>

          {/* Daily session reset notice */}
          <div className="flex items-start gap-2.5 p-3.5 rounded-xl border border-accent-200/60 bg-accent-50/40">
            <i className="ri-moon-line text-accent-700 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-accent-900 leading-relaxed">
              All sessions are automatically logged out daily at 00:00. You&apos;ll simply need to sign in again.
            </p>
          </div>

          {/* Divider + footer */}
          <div className="pt-2">
            <div className="border-t border-background-200 mb-4" />
            <div className="flex items-center justify-between text-xs text-foreground-500">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="flex items-center gap-1.5 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line" />
                Back to Website
              </button>
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                Secure session
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}